import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import VideoReel from "@/components/VideoReel";
import {
  ArrowRight, Upload, Bot, Send, Scissors, Captions, Music, Ratio,
  LayoutTemplate, Sparkles, Instagram, Facebook, Search, Tv, Globe,
  MessageCircle, Download, Wallet, Clapperboard, Smartphone
} from "lucide-react";

const CTA = "/kunden/registrieren";

const STEPS = [
  { Icon: Upload, n: "1", title: "Clips hochladen", text: "Lade deine Handy-Clips und Fotos hoch – oder nimm direkt im Browser auf. Egal ob hochkant oder quer." },
  { Icon: Bot, n: "2", title: "Robbie macht den Rohschnitt", text: "Robbie schneidet, setzt automatische Untertitel und legt passende Musik darunter. In Minuten statt Stunden." },
  { Icon: Send, n: "3", title: "Du gibst frei – fertig", text: "Kurz prüfen, feinschleifen und mit einem Klick auf Social Media posten, auf die Bildschirme schicken oder herunterladen." },
];

const FEATURES = [
  { Icon: Scissors, title: "Timeline & Schnitt", text: "Clips trimmen, teilen und anordnen – mit einer Timeline, die so leicht ist wie auf dem Handy." },
  { Icon: Captions, title: "Automatische Untertitel", text: "Sprache wird erkannt und als Untertitel eingeblendet – im eigenen Stil, mit einem Tipp korrigierbar." },
  { Icon: Music, title: "Musik & Voiceover", text: "Hinterlege lizenzfreie Musik oder sprich direkt eine Stimme ein. Lautstärke automatisch ausbalanciert." },
  { Icon: Ratio, title: "Alle Formate", text: "9:16 fürs Reel, 1:1 für den Feed, 16:9 für YouTube & Bildschirme – ein Projekt, jedes Format." },
  { Icon: LayoutTemplate, title: "Vorlagen im eigenen Design", text: "Intros, Logo und Farben aus deinem Master-Layout sitzen automatisch in jedem Video." },
  { Icon: Sparkles, title: "Robbie-KI", text: "Highlights finden, Pausen rauswerfen, Titel vorschlagen – Robbie nimmt dir die Fleißarbeit ab." },
];

const KANAELE = [
  { Icon: Instagram, label: "Instagram", sub: "Reel & Story" },
  { Icon: Facebook, label: "Facebook", sub: "Reel & Story" },
  { Icon: Search, label: "Google", sub: "Unternehmensprofil" },
  { Icon: Tv, label: "Bildschirme", sub: "Digital Signage" },
  { Icon: Globe, label: "Website", sub: "Eingebettet" },
  { Icon: MessageCircle, label: "WhatsApp", sub: "Status" },
  { Icon: Download, label: "Download", sub: "MP4 in Topqualität" },
];

const SHOWCASE = [
  { src: "/video-lp/robbie-5.mp4", poster: "/video-lp/robbie-5-poster.jpg", label: "E-Ticketing" },
  { src: "/video-lp/robbie-2.mp4", poster: "/video-lp/robbie-2-poster.jpg", label: "Social Media" },
  { src: "/video-lp/robbie-3.mp4", poster: "/video-lp/robbie-3-poster.jpg", label: "Gastronomie" },
  { src: "/video-lp/robbie-4.mp4", poster: "/video-lp/robbie-4-poster.jpg", label: "Einzelhandel" },
  { src: "/video-lp/robbie-1.mp4", poster: "/video-lp/robbie-1-poster.jpg", label: "Unternehmer" },
];

const CREDITS = [
  { title: "Wallet", text: "Dein Guthaben fürs Studio. Lädst du einmal auf, rechnest du jeden Export sekundengenau ab – ohne Abo-Falle." },
  { title: "Startguthaben", text: "Zum Testen gibt's Guthaben geschenkt. So probierst du Schnitt, Untertitel und Export aus, bevor du dich festlegst." },
  { title: "Projekt-Kontingent", text: "Jedes Paket bringt fertige Projekte pro Monat mit. Brauchst du mehr, ziehst du einfach aus dem Wallet nach." },
];

const FAQ = [
  { q: "Brauche ich Vorkenntnisse?", a: "Nein. Du lädst deine Clips hoch, Robbie macht den Rohschnitt samt Untertiteln und Musik – du gibst nur noch frei. Wer tiefer schneiden will, nutzt die Timeline, muss aber nicht." },
  { q: "Funktioniert es am Handy?", a: "Ja, komplett. Das Studio läuft direkt im Browser – auf dem Handy, Tablet und am Rechner. Schneiden wie in den großen Apps, nur einfacher und direkt mit deinem Social Media verbunden." },
  { q: "Wem gehören die Videos?", a: "Dir. Alles, was du erstellst, gehört zu 100 % dir und deinem Betrieb. Du kannst jedes Video jederzeit in Topqualität herunterladen." },
  { q: "Was kostet ein Export?", a: "Projekte aus deinem Paket-Kontingent sind inklusive. Darüber hinaus zahlst du sekundengenau aus deinem Wallet – transparent, ohne versteckte Kosten. Alle Preise findest du unter Preise." },
];

const StepCard = ({ Icon, n, title, text }) => (
  <div className="flex gap-4 rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[6px_6px_0_#111]" data-testid={`vs-step-${n}`}>
    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#FDD105] text-[#111]"><Icon size={22} /></span>
    <div>
      <div className="flex items-center gap-2"><span className="font-display text-xs font-extrabold text-[#111]/40">Schritt {n}</span></div>
      <h3 className="font-display text-lg font-bold uppercase leading-tight">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-[#555]">{text}</p>
    </div>
  </div>
);

export default function VideostudioPage() {
  return (
    <main className="bg-[#FBFAF5] text-[#111]" data-testid="videostudio-landing">
      <Seo
        title="Videostudio – Video schneiden online, direkt posten | DIMEDIAL Studio"
        description="Clips hochladen, Robbie macht den Rohschnitt mit automatischen Untertiteln und Musik – du gibst frei und postest mit einem Klick auf Social Media, Bildschirme & Co. Schneiden wie in den großen Apps, nur einfacher. 30 Tage risikofrei testen."
        path="/videostudio"
        keywords="Videostudio, Video schneiden online, Untertitel automatisch, Reels erstellen, Video Editor Browser, Social Media Video"
      />

      {/* 1 · Hero */}
      <section className="relative overflow-hidden border-b-2 border-[#111] bg-[#FDD105]">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="vs-hero-badge"><Clapperboard size={14} /> Profi-Schnittprogramm. Online.</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="vs-hero-headline">Noch nie<br />war Video<br /><span className="bg-[#111] px-2 text-[#FDD105]">so einfach.</span></h1>
            <p className="mt-5 max-w-md text-base font-medium leading-relaxed text-[#2b2b2b] sm:text-lg" data-testid="vs-hero-sub">Clips hochladen – Robbie macht den Rohschnitt – du gibst frei. Schneiden wie in den großen Apps, nur einfacher und direkt mit deinem Social Media verbunden.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={CTA} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#111] px-6 py-3 text-sm font-bold uppercase text-white shadow-[5px_5px_0_rgba(0,0,0,0.25)] transition-transform hover:-translate-y-0.5" data-testid="vs-hero-cta">30 Tage risikofrei testen <ArrowRight size={16} /></Link>
              <a href="#beispiele" className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3 text-sm font-bold uppercase transition-transform hover:-translate-y-0.5" data-testid="vs-hero-more">Editor ansehen</a>
            </div>
            <p className="mt-3 text-xs font-semibold text-[#111]/70">30 Tage kostenlos · keine Zahlungsdaten · Startguthaben inklusive</p>
          </div>

          {/* Laptop- + Handy-Mockup */}
          <div id="editor" className="relative">
            <div className="vs-float mx-auto w-full max-w-lg">
              {/* Laptop */}
              <div className="overflow-hidden rounded-t-xl border-2 border-[#111] bg-[#111] p-2 shadow-[10px_10px_0_#111]">
                <img src="/video-lp/hero_editor.jpg" alt="Videostudio-Editor mit Timeline und Filmstreifen" className="w-full rounded-md" width="1512" height="795" loading="eager" />
              </div>
              <div className="mx-auto h-3 w-[86%] rounded-b-xl border-x-2 border-b-2 border-[#111] bg-[#1a1a1a]" aria-hidden="true" />
              {/* Handy */}
              <div className="vs-float-slow absolute -bottom-6 -left-2 w-28 rotate-[-4deg] overflow-hidden rounded-[1.4rem] border-[3px] border-[#111] bg-[#141414] text-white shadow-[6px_6px_0_#111] sm:-left-6 sm:w-36" data-testid="vs-phone-mockup">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-[6px] font-semibold text-white/60 sm:text-[8px]">Rückgängig</span>
                  <span className="rounded bg-[#FDD105] px-1.5 py-0.5 text-[6px] font-bold text-[#111] sm:text-[8px]">Exportieren</span>
                </div>
                <div className="relative mx-2 grid aspect-[9/12] place-items-center rounded-md bg-gradient-to-br from-[#2d2d2d] to-[#0d0d0d]">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-white/15 sm:h-8 sm:w-8">
                    <span className="ml-0.5 h-0 w-0 border-y-[4px] border-l-[7px] border-y-transparent border-l-white sm:border-y-[5px] sm:border-l-[9px]" />
                  </span>
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[6px] text-white/80 sm:text-[7px]">0:12 / 0:30</span>
                </div>
                <div className="mx-2 mt-1.5 flex gap-0.5 overflow-hidden rounded-sm">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className={`h-2.5 flex-1 rounded-[2px] sm:h-3 ${i === 1 ? "bg-[#FDD105]" : "bg-[#3a3a3a]"}`} />
                  ))}
                </div>
                <div className="mt-1.5 flex items-center justify-around border-t border-white/10 px-1 py-1.5">
                  {[[Scissors, "Schnitt"], [Music, "Musik"], [Captions, "Text"], [Sparkles, "Effekte"]].map(([Icon, label]) => (
                    <span key={label} className="flex flex-col items-center gap-0.5 text-white/70">
                      <Icon size={11} />
                      <span className="text-[5.5px] font-semibold sm:text-[7px]">{label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · So funktioniert's */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20" data-testid="vs-steps">
        <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-xs font-bold uppercase"><Bot size={14} /> Mit Robbie</span>
        <h2 className="mt-4 font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="vs-steps-title">In 3 Schritten<br />zum fertigen Video.</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {STEPS.map((s) => <StepCard key={s.n} {...s} />)}
        </div>
      </section>

      {/* 3 · Feature-Raster */}
      <section className="border-y-2 border-[#111] bg-white" data-testid="vs-features">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
          <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="vs-features-title">Alles drin,<br />was ein Video braucht.</h2>
          <p className="mt-2 max-w-xl text-base text-[#5C574F]">Profi-Werkzeuge, ohne Profi zu sein. Und alles im selben Konto wie deine Sichtbarkeit.</p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ Icon, title, text }) => (
              <div key={title} className="flex flex-col gap-4 rounded-2xl border-2 border-[#111] bg-[#FBFAF5] p-6 shadow-[6px_6px_0_#111] transition-transform hover:-translate-y-1" data-testid={`vs-feature-${title}`}>
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#FDD105] text-[#111]"><Icon size={24} /></span>
                <h3 className="font-display text-lg font-bold uppercase leading-tight">{title}</h3>
                <p className="text-sm leading-relaxed text-[#444]">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · Ein Video – überall */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20" data-testid="vs-channels">
        <div className="rounded-3xl border-2 border-[#111] bg-[#111] p-6 shadow-[8px_8px_0_#111] md:p-10">
          <div className="text-center">
            <h2 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl" data-testid="vs-channels-title">Ein Video – <span className="text-[#FDD105]">überall.</span></h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-white/70">Fertig geschnitten? Mit einem Klick landet dein Video dort, wo deine Kundschaft ist.</p>
          </div>
          <div className="mt-8 flex flex-col items-center gap-6">
            <span className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#FDD105] bg-[#FDD105] px-5 py-3 font-display text-base font-extrabold uppercase text-[#111]"><Clapperboard size={20} /> Dein Videostudio</span>
            <div className="h-6 w-px bg-white/25" aria-hidden="true" />
            <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
              {KANAELE.map(({ Icon, label, sub }) => (
                <div key={label} className="flex flex-col items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.04] p-4 text-center transition-colors hover:border-[#FDD105]/60" data-testid={`vs-channel-${label}`}>
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-[#111]"><Icon size={20} /></span>
                  <span className="text-[13px] font-bold text-white">{label}</span>
                  <span className="text-[11px] text-white/55">{sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 · Showcase */}
      <section id="beispiele" className="scroll-mt-20 border-y-2 border-[#111] bg-[#FBFAF5]" data-testid="vs-showcase">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
          <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="vs-showcase-title">So sehen Videos aus,<br />die mit DIMEDIAL entstehen.</h2>
          <p className="mt-2 max-w-xl text-base text-[#5C574F]">Fünf echte Robbie-Kampagnen. Tippe auf den Lautsprecher für den Ton.</p>
          <div className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 pr-5 md:grid md:grid-cols-5 md:gap-5 md:overflow-visible md:pr-0">
            {SHOWCASE.map((v, i) => (
              <div key={v.label} className="w-52 shrink-0 snap-center pt-1 md:w-auto">
                <VideoReel src={v.src} poster={v.poster} label={v.label} testId={`vs-reel-${i + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · Credits & Pakete */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20" data-testid="vs-credits">
        <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-xs font-bold uppercase"><Wallet size={14} /> Transparent abgerechnet</span>
        <h2 className="mt-4 font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="vs-credits-title">Credits & Pakete.<br />Ohne Kleingedrucktes.</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {CREDITS.map(({ title, text }) => (
            <div key={title} className="rounded-2xl border-2 border-[#111] bg-white p-6 shadow-[6px_6px_0_#111]" data-testid={`vs-credit-${title}`}>
              <h3 className="font-display text-lg font-bold uppercase leading-tight">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#444]">{text}</p>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Link to="/preise" className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3 text-sm font-bold uppercase transition-transform hover:-translate-y-0.5" data-testid="vs-credits-preise">Alle Preise ansehen <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* 7 · FAQ */}
      <section className="border-y-2 border-[#111] bg-white" data-testid="vs-faq">
        <div className="mx-auto max-w-3xl px-5 py-14 md:py-16">
          <h2 className="font-display text-2xl font-extrabold uppercase sm:text-3xl" data-testid="vs-faq-title">Häufige Fragen</h2>
          <div className="mt-6 space-y-4">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="rounded-2xl border-2 border-[#111] bg-[#FBFAF5] p-5 shadow-[4px_4px_0_#111]" data-testid={`vs-faq-item-${q}`}>
                <h3 className="font-display text-base font-bold uppercase leading-tight sm:text-lg">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#444]">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 · Abschluss-CTA */}
      <section className="relative overflow-hidden border-t-2 border-[#111] bg-[#111]">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:py-20">
          <span className="inline-flex -rotate-3 items-center gap-2 rounded-xl border-2 border-[#FDD105] bg-[#FDD105] px-4 py-2 font-display text-sm font-extrabold uppercase text-[#111] shadow-[4px_4px_0_rgba(253,209,5,0.3)]" data-testid="vs-cta-stamp"><Smartphone size={16} /> 30 Tage risikofrei testen</span>
          <h2 className="mt-6 font-display text-3xl font-extrabold uppercase text-white sm:text-4xl lg:text-5xl" data-testid="vs-cta-headline">Dein erstes Video<br />ist näher, als du denkst.</h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-white/75">Lade deine Clips hoch, lass Robbie schneiden und poste mit einem Klick. 30 Tage kostenlos, ohne Zahlungsdaten – deine Sichtbarkeit läuft im selben Konto gleich mit.</p>
          <Link to={CTA} className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-8 py-4 text-base font-bold uppercase text-[#111] shadow-[6px_6px_0_rgba(253,209,5,0.3)] transition-transform hover:-translate-y-1" data-testid="vs-cta-button">Jetzt kostenlos starten <ArrowRight size={18} /></Link>
        </div>
      </section>

      <style>{`
        @keyframes vsFloat { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-10px) } }
        @keyframes vsFloatSlow { 0%,100%{ transform: translateY(0) rotate(-4deg) } 50%{ transform: translateY(-6px) rotate(-4deg) } }
        .vs-float { animation: vsFloat 6s ease-in-out infinite; }
        .vs-float-slow { animation: vsFloatSlow 7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .vs-float, .vs-float-slow { animation: none; } }
      `}</style>
    </main>
  );
}
